import { useEffect, useId, useRef, type ReactNode } from "react";
import { useLocale } from "@/locale/LocaleContext";
import { Button } from "./Button";
import styles from "./ui.module.css";

type Props = {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  confirmDisabled?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled]):not([type='hidden'])",
  "textarea:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

function isVisible(el: HTMLElement): boolean {
  if (el.getAttribute("aria-hidden") === "true") return false;
  const style = window.getComputedStyle(el);
  if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") {
    return false;
  }
  if (Number(style.opacity) === 0) return false;
  return el.getClientRects().length > 0;
}

function getFocusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) =>
      !el.hasAttribute("disabled") &&
      el.getAttribute("aria-disabled") !== "true" &&
      isVisible(el),
  );
}

export function ConfirmSheet({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel,
  danger,
  confirmDisabled,
  busy = false,
  onConfirm,
  onClose,
}: Props) {
  const { m } = useLocale();
  const titleId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const resolvedCancel = cancelLabel ?? m.common.cancel;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      const active = document.activeElement;
      openerRef.current = active instanceof HTMLElement ? active : null;
      if (!dialog.open) dialog.showModal();
      if (!dialog.hasAttribute("tabindex")) dialog.tabIndex = -1;
      const focusables = getFocusables(dialog);
      (focusables[0] ?? dialog).focus();
    } else if (dialog.open) {
      dialog.close();
      const opener = openerRef.current;
      openerRef.current = null;
      queueMicrotask(() => {
        if (opener && typeof opener.focus === "function" && document.contains(opener)) {
          opener.focus();
        }
      });
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const onCancel = (event: Event) => {
      event.preventDefault();
      if (!busyRef.current) onClose();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !dialog.open) return;

      const focusables = getFocusables(dialog);
      if (focusables.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !dialog.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener("cancel", onCancel);
    dialog.addEventListener("keydown", onKeyDown);
    return () => {
      dialog.removeEventListener("cancel", onCancel);
      dialog.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      className={styles.sheet}
      aria-labelledby={titleId}
      onClose={() => {
        if (open) onClose();
      }}
    >
      <h2 id={titleId} className="display" style={{ margin: 0, fontSize: "1.25rem" }}>
        {title}
      </h2>
      <div>{children}</div>
      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          {resolvedCancel}
        </Button>
        <Button
          variant={danger ? "danger" : "primary"}
          onClick={onConfirm}
          disabled={confirmDisabled || busy}
          aria-busy={busy || undefined}
        >
          {busy ? m.common.sending : confirmLabel}
        </Button>
      </div>
    </dialog>
  );
}
